/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState, useRef } from 'react';
import {
  StyledMainBundleContainer,
  StyledTabNav,
  StyledSectionHeader,
  StyledMenuDetailsContainer,
  StyledMenuBody,
} from './MenuModalBody.styles';
import { scrollSection, useGetBreakpoint } from '../../../../modules/utils';
import clsx from 'clsx';
import { addonItem, packageBundle, sectionBundle, sectionItem } from '../../../../modules/utils/constants';
import MenuDetailsItem from '../MenuItem/MenuItem';
import { Button } from '@marriott/mi-ui-library';

// This component can be used to render Menu/Group Bundles
export const MenuModalBody = ({ menuDetails, showList, addOn, packages, chooseFromText }: any) => {
  const [tabList, setTabList] = useState<any>();
  let portSize = useGetBreakpoint();
  const [isMobileViewPort, setIsMobileViewPort] = useState(portSize === 'mobile' || portSize === 'tablet');
  const isPackageBundle = menuDetails?.bundles?.package?.length > 0;
  const [sections, setSections] = useState<any>();
  const [, setActiveSection] = useState('');
  const [selectedTabIndex, setselectedTabIndex] = useState<number>(0);
  const scrollSectionRef = useRef<HTMLUListElement | null>(null);

  useEffect(() => {
    const tabNames: string[] = [];
    if (menuDetails?.bundles?.package?.length > 0) tabNames.push(packages);
    if (menuDetails?.items) {
      menuDetails?.items.forEach((menuItem: any) => {
        if (
          menuItem.groupContents?.length > 0 ||
          menuItem.groupBundles?.package !== null ||
          menuItem.groupBundles?.addons !== null
        ) {
          tabNames.push(menuItem.name);
        }
      });
    }
    setTabList(tabNames);
    setActiveSection(tabNames[0]);
    setSections(tabNames?.map(tabItem => document.getElementById(tabItem)));
  }, [menuDetails]);

  useEffect(() => {
    function handleResize() {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      portSize = useGetBreakpoint();
      setIsMobileViewPort(portSize === 'mobile' || portSize === 'tablet');
    }

    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const modalBody = document.getElementById('menu-modal-body');

    modalBody?.addEventListener('scroll', handleScroll);

    return () => modalBody?.removeEventListener('scroll', handleScroll);
  }, [sections]);

  useEffect(() => {
    scrollSection(scrollSectionRef);
  }, [menuDetails]);

  const scrollToSection = (sectionId: string) => {
    const section = document.getElementById(sectionId);
    section?.scrollIntoView();
    setActiveSection(sectionId);
  };

  const handleScroll = () => {
    (document.activeElement as HTMLElement).blur();
    const modalBody: any = document.getElementById('menu-modal-body');

    sections.forEach((section: any, index: number) => {
      const sectionTop = section.offsetTop - 100; // Adjusted for better accuracy
      const sectionBottom = sectionTop + section.clientHeight;

      if (modalBody.scrollTop >= sectionTop && modalBody.scrollTop < sectionBottom) {
        setselectedTabIndex(index);
        const activeMenuItem = document.getElementById(`tab${sections[index].id}`);
        if (activeMenuItem) {
          activeMenuItem.scrollIntoView({ block: 'nearest', inline: 'center' });
        }
      }
    });
  };

  if (tabList?.length < 0) return null;
  else {
    return (
      <>
        <StyledTabNav role="tablist" className="m-standard-tab-list" id="scrollable-section" ref={scrollSectionRef}>
          {tabList?.map((tabItem: string, index: number) => (
            <li
              role="tab"
              className={clsx('m-standard-tab-list-item m-0 pt-3', index === selectedTabIndex ? 'active' : '')}
              onClick={e => {
                setselectedTabIndex(index);
                e.currentTarget.focus();
                scrollToSection(tabItem);
              }}
              key={index}
              tabIndex={index === selectedTabIndex ? 0 : -1}
              id={`tab${tabItem}`}
            >
              <Button data-toggle="tab" aria-controls="" aria-selected="true">
                <span className="item-heading t-subtitle-m">{tabItem}</span>
              </Button>
            </li>
          ))}
        </StyledTabNav>

        <StyledMenuBody showList={showList} id="menu-modal-body" className="mb-3">
          <StyledMenuDetailsContainer className="container">
            {/* render menu level bundle and addon*/}
            {menuDetails?.bundles?.package?.length > 0 && (
              <section className="mb-2" id={packages}>
                <StyledSectionHeader
                  className={clsx(
                    isMobileViewPort ? 't-subtitle-m rnb-menubody-rtl' : 't-subtitle-xl rnb-menubody-rtl'
                  )}
                >
                  {packages}
                </StyledSectionHeader>
                <StyledMainBundleContainer className="d-flex flex-column">
                  {menuDetails?.bundles?.package?.map((menuBundleItem: any) => (
                    <MenuDetailsItem
                      itemType={packageBundle}
                      item={menuBundleItem}
                      key={menuBundleItem.name}
                      isPackageBundle={isPackageBundle}
                    />
                  ))}
                  {menuDetails?.bundles?.addons?.length > 0 &&
                    menuDetails?.bundles?.addons?.map((menuAddonItem: any) => (
                      <MenuDetailsItem
                        itemType={addonItem}
                        item={menuAddonItem}
                        key={menuAddonItem.name}
                        isPackageBundle={false}
                        addOn={addOn}
                      />
                    ))}
                </StyledMainBundleContainer>
              </section>
            )}

            {menuDetails?.items?.map((item: any) => (
              <section className="mb-2" id={item.name} key={item.name}>
                <StyledSectionHeader
                  className={clsx(
                    isMobileViewPort ? 't-subtitle-m rnb-menubody-rtl' : 't-subtitle-xl rnb-menubody-rtl'
                  )}
                >
                  {item.name}
                </StyledSectionHeader>
                {/* Section Bundle - Package & addon, menu items, diet and allergens list*/}
                <div className="d-flex flex-column section-item">
                  {item?.groupBundles?.package?.length > 0 && (
                    <>
                      <StyledMainBundleContainer className="d-flex flex-column">
                        {item?.groupBundles?.package.map((groupBundleItem: any) => (
                          <MenuDetailsItem
                            itemType={sectionBundle}
                            item={groupBundleItem}
                            key={groupBundleItem.name}
                            isPackageBundle={
                              isPackageBundle || (item?.groupBundles?.package?.length > 0 ? true : false)
                            }
                          />
                        ))}

                        {item?.groupBundles?.addons?.length > 0 &&
                          item?.groupBundles?.addons.map((addonItem: any) => (
                            <MenuDetailsItem
                              itemType={addonItem}
                              item={addonItem}
                              key={addonItem.name}
                              isPackageBundle={false}
                              addOn={addOn}
                            />
                          ))}
                      </StyledMainBundleContainer>
                      <span className="t-font-alt-xs px-md-5 px-lg-5 px-xl-5">{chooseFromText}</span>
                    </>
                  )}

                  {item?.groupContents?.length > 0 &&
                    item?.groupContents?.map((groupContentItem: any, index: number) => (
                      <div className="d-flex flex-column section-item" key={groupContentItem.name}>
                        <MenuDetailsItem
                          itemType={sectionItem}
                          item={groupContentItem}
                          key={groupContentItem.name}
                          className="mx-5"
                          isPackageBundle={isPackageBundle || (item?.groupBundles?.package?.length > 0 ? true : false)}
                        />
                        {index !== item?.groupContents?.length - 1 && (
                          <hr
                            className={clsx(
                              isMobileViewPort ? 'section-separator m-0 w-100' : 'section-separator my-0 mx-5'
                            )}
                          />
                        )}
                      </div>
                    ))}
                </div>
              </section>
            ))}
          </StyledMenuDetailsContainer>
        </StyledMenuBody>
      </>
    );
  }
};
export default MenuModalBody;
